import { supabase } from '../lib/supabase';
import { pushNotificationService } from './pushNotificationService';

export interface NotificacaoItem {
  id: number;
  pessoa_id: number;
  titulo: string;
  mensagem: string;
  lida: boolean;
  link_redirecionamento?: string | null;
  tipo: 'info' | 'alerta' | 'solicitacao_localizacao' | 'urgente';
  created_at: string;
}

export interface RastreamentoItem {
  id?: number;
  motorista_id: number;
  ordem_de_servico_id?: number | null;
  latitude: number;
  longitude: number;
  precisao_metros?: number | null;
  velocidade?: number | null;
  bateria_percentual?: number | null;
  online?: boolean;
  created_at?: string;
}

export const rastreamentoNotificacaoService = {
  /**
   * Envia uma notificação para um motorista ou usuário (salva na tabela notificacoes do Supabase)
   */
  async enviarNotificacao(dados: {
    pessoa_id: number;
    titulo: string;
    mensagem: string;
    tipo?: 'info' | 'alerta' | 'solicitacao_localizacao' | 'urgente';
    link_redirecionamento?: string;
  }): Promise<{ success: boolean; data?: NotificacaoItem; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('notificacoes')
        .insert({
          pessoa_id: dados.pessoa_id,
          titulo: dados.titulo,
          mensagem: dados.mensagem,
          tipo: dados.tipo || 'info',
          link_redirecionamento: dados.link_redirecionamento || null,
          lida: false
        })
        .select()
        .single();

      if (error) {
        console.error('[rastreamentoNotificacaoService] Erro ao gravar notificação:', error);
        return { success: false, error: error.message };
      }

      // Dispara evento no navegador para atualização instantânea caso esteja no mesmo client/aba
      window.dispatchEvent(new CustomEvent('nova_notificacao_operacional', { detail: data }));

      return { success: true, data };
    } catch (err: any) {
      console.error('[rastreamentoNotificacaoService] Falha inesperada ao enviar notificação:', err);
      return { success: false, error: err.message || 'Falha de comunicação' };
    }
  },

  /**
   * Solicita atualização imediata de localização GPS ao motorista
   */
  async solicitarLocalizacaoMotorista(motoristaId: number, motoristaNome?: string): Promise<{ success: boolean; error?: string }> {
    const nomeDestino = motoristaNome ? ` ${motoristaNome}` : '';
    const res = await this.enviarNotificacao({
      pessoa_id: motoristaId,
      titulo: '📍 Solicitação de Localização GPS',
      mensagem: `A Central Operacional solicitou a atualização imediata da sua localização em tempo real. Toque para transmitir sinal GPS.`,
      tipo: 'solicitacao_localizacao',
      link_redirecionamento: '/area-motorista/roteiro'
    });

    if (res.success) {
      // Dispara evento global de solicitação
      window.dispatchEvent(new CustomEvent('solicitacao_gps_enviada', {
        detail: { motoristaId, timestamp: new Date().toISOString() }
      }));
    }

    return res;
  },

  /**
   * Registra uma coordenada de GPS na tabela rastreamento_motorista
   */
  async registrarLocalizacao(dados: {
    motorista_id: number;
    ordem_de_servico_id?: number | null;
    latitude: number;
    longitude: number;
    precisao_metros?: number;
    velocidade?: number;
    bateria_percentual?: number;
    online?: boolean;
  }): Promise<{ success: boolean; error?: string; data?: RastreamentoItem }> {
    try {
      const { data, error } = await supabase
        .from('rastreamento_motorista')
        .insert({
          motorista_id: dados.motorista_id,
          ordem_de_servico_id: dados.ordem_de_servico_id || null,
          latitude: dados.latitude,
          longitude: dados.longitude,
          precisao_metros: dados.precisao_metros || 10,
          velocidade: dados.velocidade || 0,
          bateria_percentual: dados.bateria_percentual || 100,
          online: dados.online !== false
        })
        .select()
        .single();

      if (error) {
        console.error('[rastreamentoNotificacaoService] Erro ao gravar rastreamento:', error);
        return { success: false, error: error.message };
      }

      // Notifica atualização em tempo real
      window.dispatchEvent(new CustomEvent('localizacao_motorista_atualizada', {
        detail: data
      }));

      return { success: true, data };
    } catch (err: any) {
      console.error('[rastreamentoNotificacaoService] Falha ao registrar localização:', err);
      return { success: false, error: err.message };
    }
  },

  /**
   * Obtém as últimas localizações de todos os motoristas registrados recentemente
   */
  async getUltimasLocalizacoes(): Promise<Map<number, RastreamentoItem>> {
    const mapa = new Map<number, RastreamentoItem>();
    try {
      const { data, error } = await supabase
        .from('rastreamento_motorista')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error || !data) {
        console.warn('[rastreamentoNotificacaoService] Erro ao carregar posições:', error);
        return mapa;
      }

      // Filtra a mais recente de cada motorista
      data.forEach((item: RastreamentoItem) => {
        if (!mapa.has(item.motorista_id)) {
          mapa.set(item.motorista_id, item);
        }
      });

      return mapa;
    } catch (err) {
      console.error('[rastreamentoNotificacaoService] Falha ao buscar últimas localizações:', err);
      return mapa;
    }
  },

  /**
   * Busca notificações para um determinado motorista/pessoa
   */
  async getNotificacoesMotorista(pessoaId: number, apenasNaoLidas = false): Promise<NotificacaoItem[]> {
    try {
      let query = supabase
        .from('notificacoes')
        .select('*')
        .eq('pessoa_id', pessoaId)
        .order('created_at', { ascending: false });

      if (apenasNaoLidas) {
        query = query.eq('lida', false);
      }

      const { data, error } = await query.limit(30);
      if (error || !data) {
        return [];
      }

      return data as NotificacaoItem[];
    } catch (err) {
      console.error('[rastreamentoNotificacaoService] Falha ao buscar notificações:', err);
      return [];
    }
  },

  /**
   * Marca notificação como lida
   */
  async marcarNotificacaoComoLida(notificacaoId: number): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('notificacoes')
        .update({ lida: true })
        .eq('id', notificacaoId);

      return !error;
    } catch (err) {
      console.error('[rastreamentoNotificacaoService] Erro ao marcar como lida:', err);
      return false;
    }
  },

  /**
   * Assina o canal Supabase Realtime para receber notificações instantâneas via WebSocket
   */
  inscreverNotificacoesRealtime(
    pessoaId: number,
    onNovaNotificacao: (notif: NotificacaoItem) => void,
    onAtualizacaoNotificacao?: (notif: NotificacaoItem) => void
  ) {
    const channelName = `realtime-notificacoes-user-${pessoaId}-${Date.now()}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notificacoes',
          filter: `pessoa_id=eq.${pessoaId}`
        },
        (payload) => {
          const novaNotif = payload.new as NotificacaoItem;
          if (novaNotif) {
            onNovaNotificacao(novaNotif);
            // Dispara notificação nativa push / som no celular
            pushNotificationService.sendNotification(novaNotif.titulo || '🔔 Nova Notificação Operacional', {
              body: novaNotif.mensagem,
              tag: `notif-${novaNotif.id}`,
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notificacoes',
          filter: `pessoa_id=eq.${pessoaId}`
        },
        (payload) => {
          const notifAtualizada = payload.new as NotificacaoItem;
          if (notifAtualizada && onAtualizacaoNotificacao) {
            onAtualizacaoNotificacao(notifAtualizada);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log(`[Supabase Realtime] Conectado ao canal de notificações do motorista #${pessoaId}`);
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  },

  /**
   * Assina o canal Supabase Realtime para monitorar alterações e novas Ordens de Serviço
   */
  inscreverOrdensServicoRealtime(
    motoristaId: number,
    onAlteracaoOS: (payload: any) => void
  ) {
    const channelName = `realtime-os-motorista-${motoristaId}-${Date.now()}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ordem_de_servico',
          filter: `motorista_id=eq.${motoristaId}`
        },
        (payload) => {
          onAlteracaoOS(payload);
          if (payload.eventType === 'INSERT') {
            const novaOS = payload.new as any;
            pushNotificationService.notifyNovaOS(novaOS.numero_os || novaOS.id, novaOS.observacoes);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  /**
   * Captura a posição via API Geolocation do navegador e envia para o banco de dados
   */
  async capturarEEnviarGPS(
    motoristaId: number,
    ordemServicoId?: number | null
  ): Promise<{ success: boolean; lat?: number; lng?: number; message?: string }> {
    // 1. Tenta obter bateria
    let bateriaPercentual = 90;
    try {
      if ('getBattery' in navigator) {
        const battery: any = await (navigator as any).getBattery();
        bateriaPercentual = Math.round((battery.level || 0.9) * 100);
      }
    } catch {
      bateriaPercentual = 85;
    }

    // 2. Tenta capturar geolocalização do dispositivo
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        // IMPORTANTE: nunca inventar uma coordenada. Uma posição fabricada
        // aparecia no painel operacional como se fosse GPS real, sem qualquer
        // indicação de que era falsa. Melhor reportar falha de verdade.
        resolve({
          success: false,
          message: 'Este dispositivo/navegador não suporta geolocalização.'
        });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const speed = position.coords.speed !== null ? Math.round((position.coords.speed || 0) * 3.6) : 28;
          const accuracy = Math.round(position.coords.accuracy || 15);

          const res = await this.registrarLocalizacao({
            motorista_id: motoristaId,
            ordem_de_servico_id: ordemServicoId,
            latitude: lat,
            longitude: lng,
            precisao_metros: accuracy,
            velocidade: speed,
            bateria_percentual: bateriaPercentual
          });

          if (res.success) {
            resolve({
              success: true,
              lat,
              lng,
              message: `GPS transmitido com sucesso (Precisão: ${accuracy}m)`
            });
          } else {
            resolve({
              success: false,
              message: res.error || 'Erro ao persistir localização'
            });
          }
        },
        async (error) => {
          console.warn('[rastreamentoNotificacaoService] Geolocation error or permission denied:', error.message);
          // IMPORTANTE: não fabricar mais uma coordenada aproximada de São Paulo
          // quando o GPS falha/permissão é negada — isso fazia o painel
          // operacional mostrar uma posição "ao vivo" completamente falsa, sem
          // nenhuma indicação visual de que não era real.
          resolve({
            success: false,
            message: 'Não foi possível obter sua localização (permissão negada ou sinal indisponível).'
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 30000
        }
      );
    });
  },

  /**
   * Busca todas as notificações recentes para painel administrativo
   */
  async getHistoricoNotificacoes(limite = 50): Promise<NotificacaoItem[]> {
    try {
      const { data, error } = await supabase
        .from('notificacoes')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limite);

      if (error || !data) return [];
      return data as NotificacaoItem[];
    } catch {
      return [];
    }
  }
};

